import React, { ComponentType, ReactNode } from "react";
import styled from "@emotion/styled";
import i18n from "@i18n";

import { trackEvent } from "@utils/api/tracker";
import Dropdown from "@components/dropdown/Dropdown";

import goto, { linkTo } from "src/pageDefinitions/goto";
import { appConfig } from "src/config";
import {
  isCoreDirect,
  isEnLanguage,
  isEsLanguage,
  isMobile,
  isUS,
  isDE,
} from "src/utils/userSegment";
import { calorieCalculatorURL } from "src/free-products/calorie-deficit-calculator/Layout";
import { macroCalculatorURL } from "src/free-products/macro-calculator/Layout";
import { personalityQuizURL } from "src/free-products/personality-quiz/Layout";
import { loginLink } from "src/utils/navigation/login";

import LanguagePicker from "./LanguagePicker";
import {
  NavBarItem,
  NavBarButton,
  ExpandingNavItem,
  SubMenuContent,
  ArrowImage,
} from "./Styles";
import ArrowRightIcon from "./images/arrow-right.svg";

type Link = {
  id: string;
  component: ComponentType<{
    languageCodeSuffix: string;
    menuOpen: boolean;
  }>;
};

const NavBarDropdown = styled(NavBarItem)`
  position: relative;
`.withComponent(Dropdown);

const NavBarDropdownItemGroup = styled(NavBarItem)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  & > div {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    padding-left: 10px;
  }
`.withComponent("div");

/**
 * Navbar links for paid funnels.
 */
export const PAID: Link[] = [];

const renderGroup = (
  menuOpen: boolean,
  title: ReactNode,
  content: ReactNode
) => {
  if (menuOpen) {
    return content;
  }

  return (
    <NavBarDropdownItemGroup inDropdown>
      <span>{title}</span>
      <div>{content}</div>
    </NavBarDropdownItemGroup>
  );
};

/**
 * Generates Navbar links for direct funnels - EN with compass redesign.
 */
export const generateDirectLinks = (): Link[] => {
  const links = [
    {
      id: "careers",
      component: ({ menuOpen }) =>
        !isEsLanguage() && (
          <NavBarItem
            menuOpen={menuOpen}
            href={`${appConfig.BLOG_DOMAIN}/${i18n.t("navBar:careersLink")}`}
            onClick={() => trackEvent("ClickedCareersNavLink")}
          >
            {i18n.t("navBar:careers")}
          </NavBarItem>
        ),
    },
    {
      id: "product",
      component: ({ menuOpen }) => {
        if (!isUS()) {
          return (
            <NavBarItem
              menuOpen={menuOpen}
              href={`${appConfig.BLOG_DOMAIN}/${i18n.t(
                "navBar:weightLossLink"
              )}`}
              onClick={() => trackEvent("ClickedLoseWeightNavLink")}
            >
              {i18n.t("navBar:product")}
            </NavBarItem>
          );
        }
        const content = (
          <>
            <NavBarItem
              href={`${appConfig.BLOG_DOMAIN}/${i18n.t(
                "navBar:weightLossLink"
              )}`}
              menuOpen={menuOpen}
              inDropdown
              onClick={() => trackEvent("ClickedLoseWeightNavLink")}
            >
              {i18n.t("navBar:loseWeight")}
            </NavBarItem>
            <NavBarItem
              href={`${appConfig.BLOG_DOMAIN}/mood/`}
              menuOpen={menuOpen}
              inDropdown
              onClick={() => trackEvent("ClickedRelieveStressNavLink")}
            >
              {i18n.t("navBar:relieveStress")}
            </NavBarItem>
            {(!isEnLanguage() || !isUS()) &&
              renderGroup(
                menuOpen,
                "Enterprise",
                <>
                  <NavBarItem
                    menuOpen={menuOpen}
                    inDropdown={!menuOpen}
                    href={`${appConfig.BLOG_DOMAIN}/noom-for-teams/`}
                    onClick={() => trackEvent("ClickedForSmallBusinessNavLink")}
                  >
                    {i18n.t("navBar:forSmallBusiness")}
                  </NavBarItem>
                  <NavBarItem
                    menuOpen={menuOpen}
                    inDropdown={!menuOpen}
                    href={`${appConfig.BLOG_DOMAIN}/business`}
                    onClick={() => trackEvent("ClickedForEmployersNavLink")}
                  >
                    {i18n.t("navBar:forEnterprise")}
                  </NavBarItem>
                </>
              )}
          </>
        );

        return menuOpen ? (
          content
        ) : (
          <NavBarDropdown
            menuOpen={menuOpen}
            title={i18n.t("navBar:product")}
            onClick={() => trackEvent("ClickedProductNavLink")}
          >
            {content}
          </NavBarDropdown>
        );
      },
    },
    {
      id: "forBusiness",
      component: ({ menuOpen }) =>
        isEsLanguage() && (
          <NavBarItem
            menuOpen={menuOpen}
            href={`${appConfig.BLOG_DOMAIN}/${i18n.t(
              "navBar:forBusinessLink"
            )}`}
            onClick={() => trackEvent("ClickedCareersNavLink")}
          >
            {i18n.t("navBar:forBusiness")}
          </NavBarItem>
        ),
    },
    {
      id: "freeTools",
      component: ({ menuOpen }) => {
        const content = (
          <>
            <NavBarItem
              href={personalityQuizURL()}
              menuOpen={menuOpen}
              inDropdown
              onClick={() => trackEvent("ClickedPersonalityQuizNavLink")}
            >
              {i18n.t("navBar:personalityQuiz")}
            </NavBarItem>
            {!isDE() && (
              <>
                <NavBarItem
                  href={calorieCalculatorURL()}
                  menuOpen={menuOpen}
                  inDropdown
                  onClick={() => trackEvent("ClickedCalorieCalculatorNavLink")}
                >
                  {i18n.t("navBar:calorieCalculator")}
                </NavBarItem>
                <NavBarItem
                  href={macroCalculatorURL()}
                  menuOpen={menuOpen}
                  inDropdown
                  onClick={() => trackEvent("ClickedMacroCalculatorNavLink")}
                >
                  {i18n.t("navBar:macroCalculator")}
                </NavBarItem>
              </>
            )}
          </>
        );

        return menuOpen ? (
          content
        ) : (
          <NavBarDropdown
            menuOpen={menuOpen}
            title={i18n.t("navBar:freeTools")}
            onClick={() => trackEvent("ClickedFreeToolsNavLink")}
          >
            {content}
          </NavBarDropdown>
        );
      },
    },
    {
      id: "company",
      component: ({ menuOpen }) => {
        const content = (
          <>
            <NavBarItem
              href={`${appConfig.BLOG_DOMAIN}/${i18n.t("navBar:aboutUsLink")}`}
              menuOpen={menuOpen}
              inDropdown={!menuOpen}
              onClick={() => trackEvent("ClickedAboutUsNavLink")}
            >
              {i18n.t("navBar:aboutUs")}
            </NavBarItem>
            {menuOpen ? null : (
              <NavBarItem
                href={`${appConfig.BLOG_DOMAIN}/${i18n.t(
                  "navBar:careersLink"
                )}`}
                inDropdown
                onClick={() => trackEvent("ClickedCareersNavLink")}
              >
                {i18n.t("navBar:careers")}
              </NavBarItem>
            )}
            <NavBarItem
              menuOpen={menuOpen}
              inDropdown={!menuOpen}
              href={`${appConfig.BLOG_DOMAIN}/${i18n.t("navBar:researchLink")}`}
              onClick={() => trackEvent("ClickedResearchNavLink")}
            >
              {i18n.t("navBar:research")}
            </NavBarItem>
            <NavBarItem
              menuOpen={menuOpen}
              inDropdown={!menuOpen}
              href={`${appConfig.BLOG_DOMAIN}/${i18n.t("navBar:pressLink")}`}
              onClick={() => trackEvent("ClickedPressNavLink")}
            >
              {i18n.t("navBar:press")}
            </NavBarItem>
            <NavBarItem
              menuOpen={menuOpen}
              inDropdown={!menuOpen}
              href={`${appConfig.BLOG_DOMAIN}/${i18n.t("navBar:blogLink")}`}
              onClick={() => trackEvent("ClickedBlogNavLink")}
            >
              {i18n.t("navBar:blog")}
            </NavBarItem>
          </>
        );

        return menuOpen ? (
          content
        ) : (
          <NavBarDropdown
            menuOpen={menuOpen}
            title={i18n.t("navBar:company")}
            onClick={() => trackEvent("ClickedCompanyNavLink")}
          >
            {content}
          </NavBarDropdown>
        );
      },
    },
    {
      id: "support",
      component: ({ menuOpen }) => {
        return (
          <NavBarItem
            menuOpen={menuOpen}
            href={`${appConfig.BLOG_DOMAIN}/${i18n.t("navBar:supportLink")}`}
            onClick={() => trackEvent("ClickedSupportNavLink")}
          >
            {i18n.t("navBar:support")}
          </NavBarItem>
        );
      },
    },
    {
      id: "login",
      component: ({ menuOpen }) => (
        <NavBarItem
          menuOpen={menuOpen}
          bold
          href={loginLink}
          onClick={() => trackEvent("ClickedLandingLoginButton")}
        >
          {i18n.t("navBar:logIn")}
        </NavBarItem>
      ),
    },
    {
      id: "language-picker",
      component: ({ menuOpen }) => <LanguagePicker menuOpen={menuOpen} />,
    },
    {
      id: "learn-more",
      component: ({ menuOpen }) => (
        <NavBarButton
          href={linkTo.mainSurvey()}
          menuOpen={menuOpen}
          onClick={(ev) => {
            ev.preventDefault();
            trackEvent("ClickedLearnMoreNavLink");
            goto.mainSurvey();
          }}
        >
          {i18n.t("navBar:learnMore")}
        </NavBarButton>
      ),
    },
  ];

  const businessLinks = {
    id: "business",
    component: ({ menuOpen }) => {
      const content = (
        <>
          <NavBarItem
            menuOpen={menuOpen}
            inDropdown={!menuOpen}
            href={`${appConfig.BLOG_DOMAIN}/business`}
            onClick={() => trackEvent("ClickedForEmployersNavLink")}
          >
            {i18n.t("navBar:forEnterprise")}
          </NavBarItem>
          <NavBarItem
            menuOpen={menuOpen}
            inDropdown={!menuOpen}
            href={`${appConfig.BLOG_DOMAIN}/noom-for-teams/`}
            onClick={() => trackEvent("ClickedForSmallBusinessNavLink")}
          >
            {i18n.t("navBar:forSmallBusiness")}
          </NavBarItem>
        </>
      );

      return menuOpen ? (
        content
      ) : (
        <NavBarDropdown
          menuOpen={menuOpen}
          large
          title={i18n.t("navBar:forBusiness")}
          onClick={() => trackEvent("ClickedForBusinessNavLink")}
        >
          {content}
        </NavBarDropdown>
      );
    },
  };
  // US-English direct hits should get the imployer links only
  if (isEnLanguage() && isUS() && isCoreDirect()) {
    return [
      {
        id: "for-organizations",
        component: ({ menuOpen }) => {
          const content = (
            <>
              <NavBarItem
                bold
                href="https://www.noom.com/for-work/?nfw_section=employers"
                menuOpen={menuOpen}
                inDropdown={!menuOpen}
                onClick={() => trackEvent("ClickedEmployersNavLink")}
              >
                {i18n.t("navBar:employers")}
                {menuOpen && <ArrowImage src={ArrowRightIcon} />}
              </NavBarItem>
              <NavBarItem
                bold
                href="https://www.noom.com/for-work/?nfw_section=healthPlans"
                inDropdown={!menuOpen}
                menuOpen={menuOpen}
                onClick={() => trackEvent("ClickedHealthPlansNavLink")}
              >
                {i18n.t("navBar:healthPlans")}
                {menuOpen && <ArrowImage src={ArrowRightIcon} />}
              </NavBarItem>
              <NavBarItem
                bold
                menuOpen={menuOpen}
                inDropdown={!menuOpen}
                href="https://www.noom.com/for-work/?nfw_section=consultantsAndBrokers"
                onClick={() =>
                  trackEvent("ClickedConsultantsAndBrokersNavLink")
                }
              >
                {i18n.t("navBar:consultantsBrokers")}
                {menuOpen && <ArrowImage src={ArrowRightIcon} />}
              </NavBarItem>
              <NavBarItem
                bold
                menuOpen={menuOpen}
                inDropdown={!menuOpen}
                href="https://www.noom.com/health-systems"
                onClick={() => trackEvent("ClickedHealthSystemsNavLink")}
              >
                {i18n.t("navBar:healthSystems")}
                {menuOpen && <ArrowImage src={ArrowRightIcon} />}
              </NavBarItem>
              <NavBarItem
                bold
                menuOpen={menuOpen}
                inDropdown={!menuOpen}
                href="https://www.noom.com/for-work/resources"
                onClick={() => trackEvent("ClickedResourcesNavLink")}
              >
                {i18n.t("navBar:resources")}
                {menuOpen && <ArrowImage src={ArrowRightIcon} />}
              </NavBarItem>
            </>
          );

          return menuOpen ? (
            <ExpandingNavItem
              bold
              tabIndex={0}
              menuOpen={menuOpen}
              inDropdown={!menuOpen}
              onClick={() => trackEvent("ClickedForOrganizationsNavLink")}
            >
              {i18n.t("navBar:forOrganizations")}
              <SubMenuContent>{content}</SubMenuContent>
            </ExpandingNavItem>
          ) : (
            <NavBarDropdown
              bold
              menuOpen={menuOpen}
              title={i18n.t("navBar:forOrganizations")}
              onClick={() => trackEvent("ClickedForOrganizationsNavLink")}
            >
              {content}
            </NavBarDropdown>
          );
        },
      },
      {
        id: "noom-med",
        component: ({ menuOpen }) => (
          <NavBarItem
            menuOpen={menuOpen}
            bold
            href="https://www.noom.com/med/"
            onClick={() => trackEvent("ClickedNoomMedNavLink")}
          >
            {i18n.t("navBar:noomMed")}
          </NavBarItem>
        ),
      },
      {
        id: "login",
        component: ({ menuOpen }) => (
          <NavBarItem
            menuOpen={menuOpen}
            bold
            href={loginLink}
            onClick={() => trackEvent("ClickedLandingLoginButton")}
          >
            {i18n.t("navBar:logIn")}
          </NavBarItem>
        ),
      },
      {
        id: "learn-more",
        component: ({ menuOpen }) => (
          <NavBarButton
            href={linkTo.mainSurvey()}
            menuOpen={menuOpen}
            onClick={(ev) => {
              ev.preventDefault();
              trackEvent("ClickedLearnMoreNavLink");
              goto.mainSurvey();
            }}
          >
            {i18n.t("navBar:getNoom")}
          </NavBarButton>
        ),
      },
    ];
  }
  // US-English language users should see employer links as a separate nav-item with ordering
  // based on mobile or desktop view
  if (isEnLanguage() && isUS()) {
    if (isMobile()) {
      links.splice(2, 0, businessLinks);
    } else {
      links.splice(1, 0, businessLinks);
    }
  }
  return links;
};
